// Read layer over the in-memory product catalog (pricesV2), shared by admin,
// public web and backend. Imports only leaf pricing modules (never the
// @/utils/pricing barrel) so the calculators can depend on it without a cycle.

import {Currency} from '@/utils/currency'
import {PriceRuleType} from '@/utils/pricing/constants'
import {sum} from '@/utils/pricing/decimals'
import {pricesV2} from '@/utils/pricing/prices'
import {ProductQuantityUnit, TieredPriceRuleConfig} from '@/utils/pricing/types'
import {ManualInterpolationProp} from '@/utils/quote/constants'
import {PriceRule, ProductWithPriceRules} from '@/utils/quote/types'

// --- low-level catalog price reads (shared with the quote builder) ---

const currencyPriceField = (currency: Currency) =>
  `price_${currency.toLowerCase()}` as keyof PriceRule['prices'][number]

const isTieredRule = (rule: PriceRule) =>
  rule.type === PriceRuleType.perTrademarkClass || rule.type === PriceRuleType.perTrademark

// Whether a product's price depends on how many units are ordered.
export const hasTieredPricing = (products: ProductWithPriceRules[]) =>
  products.some((product) => product.price_rules.some(isTieredRule))

// null when the product is flat-priced (no tiered rules).
export const getProductQuantityUnit = (
  product: ProductWithPriceRules,
): ProductQuantityUnit | null => {
  const tieredRule = product.price_rules.find(isTieredRule)

  if (!tieredRule) {
    return null
  }

  return tieredRule.type === PriceRuleType.perTrademark ? 'trademark' : 'class'
}

const rulePriceInCurrency = (rule: PriceRule | undefined, currency: Currency): number => {
  const price = rule?.prices[0]

  return price ? Number(price[currencyPriceField(currency)]) : 0
}

const tierThreshold = (rule: PriceRule): number => {
  const config = rule.config as TieredPriceRuleConfig

  return 'classes' in config ? config.classes : config.trademarks
}

// A tiered rule "applies from" its threshold onward. For a given position, the
// applicable rule is the one with the greatest threshold still <= that position
// (e.g. with tiers at 2 and 4, positions 2–3 use the "2" rule, 4+ use the "4").
const tierRuleForIndex = (tieredRules: PriceRule[], index: number): PriceRule | undefined =>
  tieredRules
    .filter((rule) => tierThreshold(rule) <= index)
    .sort((a, b) => tierThreshold(b) - tierThreshold(a))[0]

// Total price for `unitCount` units (classes or trademarks) of a single product:
//   - unit 1 is always the base (default) price, counted once;
//   - each additional unit (2..N) adds the tier rule that applies to it.
// Products with no tiered rules stay flat regardless of unitCount.
export const getUnitPrice = (
  product: ProductWithPriceRules,
  unitCount: number,
  currency: Currency,
): number => {
  const defaultRule = product.price_rules.find((rule) => rule.type === PriceRuleType.default)
  const tieredRules = product.price_rules.filter(isTieredRule)

  const unitPrices = [rulePriceInCurrency(defaultRule, currency)]
  for (let index = 2; index <= unitCount; index++) {
    unitPrices.push(rulePriceInCurrency(tierRuleForIndex(tieredRules, index), currency))
  }

  return sum(unitPrices)
}

// Manual params a product's invoice line needs, as computed by the catalog sync
// and stored on product_v2.parameters (the dialog has no locale bundle, so it
// can't derive them from the translation itself).
export const productManualInterpolationProps = (
  product: ProductWithPriceRules,
): ManualInterpolationProp[] => {
  const stored = (product.parameters as {manualInterpolationProps?: unknown} | null)
    ?.manualInterpolationProps

  return Array.isArray(stored)
    ? stored.filter((prop): prop is ManualInterpolationProp =>
        Object.values(ManualInterpolationProp).includes(prop as ManualInterpolationProp),
      )
    : []
}

// Union across a bundle's products — a bundle is quoted as one line group, so a
// param needed by any of its products is asked for once.
export const bundleManualInterpolationProps = (
  products: ProductWithPriceRules[],
): ManualInterpolationProp[] => [...new Set(products.flatMap(productManualInterpolationProps))]

// --- SKU (category + name) lookup over the pricesV2 singleton ---

// Index products by SKU (`${category}.${name}`) for O(1) lookups; rebuilt when
// the catalog size changes (e.g. after injectPricesV2 on the client).
let productIndexCache: Map<string, ProductWithPriceRules> | null = null
let indexedCatalogLength = -1

const skuKey = (category: string, name: string) => `${category}.${name}`

const getProductIndex = (): Map<string, ProductWithPriceRules> => {
  if (productIndexCache && indexedCatalogLength === pricesV2.length) {
    return productIndexCache
  }

  const index = new Map<string, ProductWithPriceRules>()
  for (const bundle of pricesV2) {
    for (const product of bundle.products) {
      index.set(skuKey(product.category, product.name), product)
    }
  }

  productIndexCache = index
  indexedCatalogLength = pricesV2.length
  return index
}

export const findCatalogProduct = (
  category: string,
  name: string,
): ProductWithPriceRules | undefined => getProductIndex().get(skuKey(category, name))

// Price for a catalog SKU across `unitCount` units — classes or trademarks,
// whichever the product is tiered by (see getProductQuantityUnit). Throws when
// the SKU is missing; use findCatalogProduct to tolerate a gap, or
// buildProductFee when you want the whole Fee rather than just the amount.
export const getCatalogUnitPrice = (
  category: string,
  name: string,
  currency: Currency,
  unitCount = 1,
): number => {
  const product = findCatalogProduct(category, name)
  if (!product) {
    throw new Error(`No catalog product found for sku "${skuKey(category, name)}"`)
  }

  return getUnitPrice(product, unitCount, currency)
}
